import type { Difficulty } from '../settings/types.js';
import { evaluate } from './BoardEvaluator.js';
import { AlphaBetaSearch } from './AlphaBetaSearch.js';
import { profileFor } from './DifficultyStrategy.js';
import type { Move, Position } from '../game/types.js';

export type Random = () => number;

/**
 * Pick the bot's move synchronously. The worker calls this; tests call it
 * directly. `budgetMs` caps the think below the profile's time (a running
 * clock), so the bot cannot lose on time by out-thinking a bullet game.
 */
export function chooseMove(pos: Position, difficulty: Difficulty, random: Random = Math.random, now: () => number = Date.now, budgetMs?: number): Move | null {
  const profile = profileFor(difficulty);
  const timeMs = budgetMs === undefined ? profile.limits.timeMs : Math.min(profile.limits.timeMs, budgetMs);
  const search = new AlphaBetaSearch({ ...profile.limits, timeMs }, now);
  if (profile.noiseCp === 0) return search.run(pos).best;

  let best: Move | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const { move, score } of search.scoreRootMoves(pos, profile.limits.maxDepth)) {
    const noisy = score + (random() * 2 - 1) * profile.noiseCp;
    if (noisy > bestScore) {
      bestScore = noisy;
      best = move;
    }
  }
  return best;
}

/** Whether the bot playing `botColor` takes a draw offer: only when it stands worse. */
export function acceptsDraw(pos: Position, botColor: Position['turn'], difficulty: Difficulty): boolean {
  const fromMover = evaluate(pos);
  const fromBot = pos.turn === botColor ? fromMover : -fromMover;
  return fromBot <= profileFor(difficulty).acceptDrawAtCp;
}
